// #genai Phase 2 saved-payment-methods list + row styles — pixel-matched to Figma 219:24003 & 239:7554.
// Rendered on /billing-information when the ENABLE_MULTI_PAYMENT_METHODS flag is ON and the user has
// at least one saved payment method.
import { makeStyles } from 'lib/components';

export default makeStyles(() => ({
  // Page column: subtitle + white card, 20px page margin (matches the empty-state / picker chrome).
  pageWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    margin: '20px',
    // #genai Mobile (Figma 331:27171): the white card after the blue hero owns the entire surface.
    // The Figma container autolayout has pad(t16, b16, l24, r24) and gap 24, so we own ALL spacing
    // at this level — subtitle, banner, card row, and list items all flow off this single 24px gap.
    // No element-level margins/padding-tops are needed below.
    '@media (max-width: 639px)': {
      margin: '0px',
      gap: '24px',
      padding: '16px 24px',
      backgroundColor: '#FFFFFF',
    },
  },

  // "Manage your payment methods (billing) below."
  subtitle: {
    fontFamily: 'Noto Sans',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    color: '#4D5365',
  },

  // #genai Success banner (Figma 331:27171) — green confirmation shown after returning from Add
  // Bank Payment Method. Rounded light-green box, icon top-aligned with the title, no border
  // hairline. On mobile it lives inside pageWrap's 24px horizontal padding, so it does NOT need
  // its own margin. !important wins over the shared Banner's default `borderRadius: 0` +
  // `border-bottom: lime-700` rules (Banner.component.js applies styles.alertContainer first, then
  // our className is merged after — using !important keeps us robust to JSS ordering).
  successBanner: {
    borderRadius: '4px !important',
    borderBottom: 'none !important',
    alignItems: 'flex-start',
    // #genai Figma 331:27276 "Banner Content" is a vertical autolayout (itemSpacing 16) with a
    // title + body. The title is Noto Sans SemiBold 18/27 #1B202C — the shared Banner renders the
    // .title at 14px, so bump it here to match the Figma exactly (scoped to this success banner).
    '& .title': {
      fontSize: '18px',
      lineHeight: '27px',
      fontWeight: 600,
    },
    // #genai The shared Banner has NO gap between the .title and .message slots on mobile (only
    // desktop >=1024px sets gap 8), so the lines hug. Force the Figma's 16px title→body gap.
    '& .title + .message': {
      marginTop: '16px',
    },
    // #genai 16px between the two body paragraphs (account-added sentence + default-bill sentence),
    // which Figma renders as a single text node with an embedded blank line.
    '& .message > div + div': {
      marginTop: '16px',
    },
  },

  // #genai Phase 2 — initial-load spinner container (centered while GET /v1/payment-methods resolves).
  loadingWrap: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '48px 0',
    color: '#A60064',
  },

  // White "Payment info" card — radius 4, padding 24, ev-elevation--z01 shadow, gap 24.
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    padding: '24px',
    borderRadius: '4px',
    backgroundColor: '#FFFFFF',
    boxShadow: '0px 1px 3px 0px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.12), 0px 0px 2px 0px rgba(0,0,0,0.14)',
    // #genai Mobile (Figma 331:27171): flat edge-to-edge white surface — the outer card drops its
    // radius/shadow AND its padding (pageWrap owns horizontal padding; card just stacks children
    // with the same 24px gap as the Figma autolayout).
    '@media (max-width: 639px)': {
      borderRadius: '0px',
      boxShadow: 'none',
      padding: '0px',
    },
  },

  // "Payment Methods" title + "Add Payment Method" button.
  cardHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '24px',
    flexWrap: 'wrap',
    // #genai Mobile (Figma 183:31478): full-width "Add Payment Method" stacked ABOVE the title.
    '@media (max-width: 639px)': {
      flexDirection: 'column-reverse',
      alignItems: 'stretch',
      gap: '24px',
    },
  },

  cardTitle: {
    fontFamily: 'Noto Sans',
    fontSize: '18px',
    fontWeight: 600,
    lineHeight: '27px',
    color: '#005A8D',
  },

  // "Add Payment Method" button + payment-type menu now live in the shared AddPaymentMethodButton.

  // List of method rows — gap 24 between rows (matches card content gap).
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },

  // ── Row card ── white bg, 1px #E5E5E5 border, shadow/md, padding 16, radius 4.
  row: {
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    width: '100%',
    padding: '16px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E5E5',
    borderRadius: '4px',
    boxShadow: '0px 4px 6px -1px rgba(0,0,0,0.1), 0px 2px 4px -2px rgba(0,0,0,0.1)',
  },

  // #genai EXPIRED row treatment (Figma 269:20146 / 269:20163): the row drops to the page-chrome
  // grey (#F0F2F7 — sampled directly from the Figma render) and loses the white-card shadow so it
  // visually recedes from the active rows. Border is kept as a subtle outline to preserve the row
  // boundary without competing with the active rows' elevation.
  rowExpired: {
    backgroundColor: '#F0F2F7',
    boxShadow: 'none',
  },

  // #genai EXPIRED pill (Figma 269:20146): same geometry as the DEFAULT pill but filled with the
  // wine/magenta #A60064 (sampled from the Figma render). Occupies the same slot as the DEFAULT
  // badge so the row alignment stays identical regardless of state.
  expiredBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '20px',
    padding: '2px 8px',
    borderRadius: '12px',
    backgroundColor: '#A60064',
    fontFamily: 'Noto Sans',
    fontSize: '10px',
    fontWeight: 700,
    lineHeight: '14px',
    textTransform: 'uppercase',
    color: '#FAFAFA',
    whiteSpace: 'nowrap',
  },

  rowHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '16px',
    width: '100%',
    // #genai Mobile: the whole header toggles expand (chevron is hidden).
    '@media (max-width: 639px)': {
      cursor: 'pointer',
    },
  },

  // Icon + text block (gap 16, icon top-aligned with the "Ending in" line).
  rowIconText: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    minWidth: 0,
  },

  rowTextBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    minWidth: 0,
  },

  // "Ending in XXXX" + (optional) Default badge on one line.
  endingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },

  endingText: {
    fontFamily: 'Noto Sans',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: '#0A0A0A',
  },

  secondaryText: {
    fontFamily: 'Noto Sans',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: '#737373',
  },

  // "Default" pill — Brand/blue #3E649B, white bold 10px uppercase, radius 12.
  defaultBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '20px',
    padding: '2px 8px',
    borderRadius: '12px',
    backgroundColor: '#3E649B',
    fontFamily: 'Noto Sans',
    fontSize: '10px',
    fontWeight: 700,
    lineHeight: '14px',
    textTransform: 'uppercase',
    color: '#FAFAFA',
    whiteSpace: 'nowrap',
  },

  // Right-side actions: Make Default / Remove links + expand chevron.
  rowActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '32px',
    flexShrink: 0,
  },

  makeDefault: {
    fontFamily: 'Noto Sans',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: '#1D4ED8',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:hover': {
      textDecoration: 'underline',
    },
  },

  remove: {
    fontFamily: 'Noto Sans',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: '#BE123C',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:hover': {
      textDecoration: 'underline',
    },
  },

  chevronBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    marginLeft: '16px',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    color: '#1B202C',
    flexShrink: 0,
  },

  chevronIcon: {
    width: '24px',
    height: '24px',
    transition: 'transform 0.15s ease',
  },

  chevronExpanded: {
    transform: 'rotate(180deg)',
  },

  // ── Expanded billing detail ── two columns, indented to align under the text column.
  detail: {
    display: 'flex',
    gap: '39px',
    marginTop: '16px',
    paddingLeft: '51px',
    '@media (max-width: 639px)': {
      flexDirection: 'column',
      gap: '16px',
      paddingLeft: '0px',
    },
  },

  detailCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '313px',
    maxWidth: '100%',
  },

  detailItem: {
    display: 'grid',
    gridTemplateColumns: '143px 1fr',
    alignItems: 'start',
    columnGap: '8px',
  },

  detailLabel: {
    fontFamily: 'Noto Sans',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: '#737373',
  },

  detailValue: {
    fontFamily: 'Noto Sans',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: '#0A0A0A',
  },

  // ── Mobile expanded detail (Figma 239:7271 / 239:7101) ──
  // Single column, short labels; actions become full-width stacked buttons. Per Figma the
  // expanded section flows directly under the header inside the same card with a 16px gap —
  // there is no horizontal divider line.
  detailMobile: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginTop: '16px',
  },

  // Label column narrow enough to wrap "Billing Address" / "Secondary Phone" to two lines per Figma.
  detailItemMobile: {
    display: 'grid',
    gridTemplateColumns: '92px 1fr',
    alignItems: 'start',
    columnGap: '16px',
  },

  // Full-width stacked action buttons under the mobile detail.
  // Figma 239:7271 spaces them with `var(--spacing/4)` = 16px.
  mobileActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginTop: '8px',
  },

  // "Make Default Payment" — filled blue, full width.
  makeDefaultBtnMobile: {
    height: '44px',
    padding: '10px 16px',
    borderRadius: '4px',
    fontFamily: 'Noto Sans',
    fontSize: '16px',
    fontWeight: 500,
    letterSpacing: '0.32px',
    textTransform: 'none',
    backgroundColor: '#007CAD',
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#005A8D',
    },
  },

  // "Remove" — filled red, full width.
  // Figma uses Utility/rose-800 #CB2523 (not the desktop link's rose-700 #BE123C).
  removeBtnMobile: {
    height: '44px',
    padding: '10px 16px',
    borderRadius: '4px',
    fontFamily: 'Noto Sans',
    fontSize: '16px',
    fontWeight: 500,
    letterSpacing: '0.32px',
    textTransform: 'none',
    backgroundColor: '#CB2523',
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#A81E1C',
    },
  },
}));
